<template>
  <v-container grid-list-lg>
    <v-layout v-bind='binding'>
      <v-flex xs12 lg6 order-lg2 class='ma-3'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Select Pulse IO
            <v-spacer/>
            <v-btn
              flat
              class='transparent ma-0'
              @click='$refs.legend.show()'
              icon
              small
            >
              <v-icon class='white--text oem-color'>
                help_outline
              </v-icon>
            </v-btn>
          </v-card-title>
          <helpLegend ref='legend' page='pulse'></helpLegend>
          <v-card-text>
          <div class='img-overlay'>
            <device-image type='pulse'></device-image>
            <div
              v-for='(input, index) in pulseInputs'
              :key='index'
              :style='buttonLocation(index)'
              class='button-holder'
            >
              <selector
                :state='pulseState(input)'
                :index='index'
                :selected='index == storeState.selectedPulse.id'
                :text='`I${index+1}`'
                v-on:selector-clicked='showPulse'
              ></selector>
            </div>
            <div
              v-for='(input, index) in pulseOutputs'
              :key='index + 2'
              :style='buttonLocation(index + 2)'
              class='button-holder'
            >
              <selector
                :state='pulseState(input)'
                :index='index + 2'
                :selected='index + 2 == storeState.selectedPulse.id'
                :text='`O${index+1}`'
                v-on:selector-clicked='showPulse'
              ></selector>
            </div>
          </div>
          </v-card-text>
        </v-card>
      </v-flex>
      <transition name='slide-x-transition'>
        <v-flex xs12 lg6 class='ma-3'>
          <v-card>
            <v-card-title v-show='!storeState.selectedPulse.out'
              class='headline font-weight-regular oem-color'>
              Pulse In {{ storeState.selectedPulse.id + 1 }} Configuration
            </v-card-title>
            <v-card-title v-show='storeState.selectedPulse.out'
              class='headline font-weight-regular oem-color'>
              Pulse Out {{ storeState.selectedPulse.id - 1 }} Configuration
            </v-card-title>
            <v-card-text>
              <div class='ma-3'>
                <v-switch
                  v-model='storeState.selectedPulse.active'
                  :label='storeState.selectedPulse.active ? "Enabled" : "Disabled"'
                  class='pt-4'
                  @change='validate()'
                ></v-switch>
                <div :style='storeState.selectedPulse.active && storeState.selectedPulse.out ? "" : "height:0px; overflow:hidden;"'>
                  <v-select
                    v-model='storeState.selectedPulse.loadId'
                    item-value='id'
                    item-text='name'
                    label='Load'
                    :items='storeState.data.loads'
                    :rules='selectRules'
                    class='pt-4'
                    v-validate="{load:[storeState]}"
                    data-vv-name="load"
                    :error-messages="errors.collect('load')"
                    @change='validate()'
                  ></v-select>
                </div>
                <div :style='storeState.selectedPulse.active ? "" : "height:0px; overflow:hidden;"'>
                  <v-select
                    v-model='storeState.selectedPulse.type'
                    label='Pulse type'
                    :items='storeState.selectedPulse.out ? storeState.pulseOutTypeOptions : storeState.pulseTypeOptions'
                    class='pt-4'
                    v-validate="{pulseType:[storeState]}"
                    data-vv-name="pulse type"
                    :error-messages="errors.collect('pulse type')"
                    @change='validate()'
                  ></v-select>
                </div>
                <div :style='storeState.selectedPulse.active ? "" : "height:0px; overflow:hidden;"'>
                  <v-select
                    v-model='storeState.selectedPulse.param'
                    label='Units of measure'
                    :items='storeState.selectedPulse.out ? storeState.pulseOutMeasureOptions[storeState.selectedPulse.type] : storeState.pulseMeasureOptions[storeState.selectedPulse.type]'
                    class='pt-4'
                    v-validate="{unitType:[storeState,storeState.pulseMeasureOptions]}"
                    data-vv-name="units of measure"
                    :error-messages="errors.collect('units of measure')"
                    @change='validate()'
                  ></v-select>
                </div>
                <div :style='storeState.selectedPulse.active ? "" : "height:0px; overflow:hidden;"'>
                  <v-text-field
                    label='Scaler'
                    v-model='storeState.selectedPulse.scaler'
                    v-validate="'required|numeric|min_value:1|max_value:1000'"
                    data-vv-name='scaler'
                    :error-messages="errors.collect('scaler')"
                    class='pt-4'
                    required
                    @change='validate()'
                  ></v-text-field>
                </div>
                <div :style='storeState.selectedPulse.active && this.storeState.selectedPulse.out ? "" : "height:0px; overflow:hidden;"'>
                  <v-text-field
                    label='Pulse Width (ms)'
                    v-model='storeState.selectedPulse.pulseWidth'
                    v-validate="'required|decimal|min_value:20|max_value:2000'"
                    hint='20 - 2000'
                    data-vv-name='pulse width'
                    :error-messages="errors.collect('pulse width')"
                    class='pt-4'
                    @change='validate()'
                  ></v-text-field>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                class='oem-button'
                flat
                @click='save'
                :disabled='errors.any()'
              >
                Save
              </v-btn>
              <v-btn
                class='oem-button'
                flat
                @click='clear()'
              >
                Defaults
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </transition>
    </v-layout>
  </v-container>
</template>

<script>
import { Validator } from 'vee-validate';
import selector from './BaseSelector.vue';
import helpLegend from './BaseLegend.vue';
import deviceImage from './BaseDeviceImage.vue';

Validator.extend('unitType', {
  getMessage: 'Please select a unit of measure.',
  validate(value, args) {
    if (!args[0].selectedPulse.active) {
      return true;
    }
    if (args[0].selectedPulse.type !== -1) {
      const allowedValues = args[1][args[0].selectedPulse.type];
      for (let i = 0; i < allowedValues.length; i++) {
        if (allowedValues[i].value === value) {
          return true;
        }
      }
    }
    return false;
  },
});
Validator.extend('pulseType', {
  getMessage: 'Please select a pulse type.',
  validate(value, args) {
    if (!args[0].selectedPulse.active) {
      return true;
    }
    return value !== -1 && args[0].selectedPulse.type !== -1;
  },
});
Validator.extend('load', {
  getMessage: 'Please select a load.',
  validate(value, args) {
    if (!args[0].selectedPulse.active) {
      return true;
    }
    if (!args[0].selectedPulse.out) {
      return true;
    }
    return value !== -1;
  },
});

export default {
  name: 'Pulse',
  components: {
    selector,
    helpLegend,
    deviceImage,
  },

  computed: {
    binding() {
      const binding = {};

      if (this.$vuetify.breakpoint.mdAndDown) binding.column = true;

      return binding;
    },
    customCt() {
      if (this.storeState.allCts[this.selectedCt].type === 9) {
        return true;
      }
      return false;
    },
    pulseInputs() {
      return this.storeState.data.pulse.filter(pulse => pulse.out === false);
    },
    pulseOutputs() {
      return this.storeState.data.pulse.filter(pulse => pulse.out === true);
    },
  },
  data() {
    return {
      storeState: this.$store.state,
      valid: true,
      helpDialog: false,
      selectRules: [v => v !== -1 || 'Field is required'],
      buttonLocation12: [
        { top: '70.0%', left: '29%' },
        { top: '70.0%', left: '23%' },
        { top: '70.0%', left: '35%' },
      ],
      buttonLocation48: [
        { top: '27%', left: '37%' },
        { top: '27%', left: '31%' },
        { top: '27%', left: '43%' },
      ],
    };
  },
  methods: {
    buttonLocation(index) {
      if (this.storeState.data.deviceInfo.model === 48) {
        return this.buttonLocation48[index];
      }
      return this.buttonLocation12[index];
    },
    compareInts(a, b) {
      if (Number(a) === Number(b)) {
        return true;
      }
      return false;
    },
    showPulse(index) {
      this.$nextTick(() => {
        this.storeState.selectedPulse = JSON.parse(JSON.stringify(this.storeState.data.pulse[index]));
        if (this.storeState.selectedPulse.active === false) {
          this.storeState.selectedPulse.type = -1;
          this.storeState.selectedPulse.param = -1;
          this.storeState.selectedPulse.loadId = -1;
        }
        if (this.storeState.selectedPulse.out === true) {
          this.storeState.selectedPulse.type = 4;
        }
        this.validate();
        this.$nextTick(() => {
          this.validate();
        });
      });
    },
    pulseState(pulse) {
      let state = 0;
      if (pulse.active) {
        state = 0;
      } else {
        state = 3;
      }
      return state;
    },
    save() {
      this.$nextTick(() => {
        this.$validator.validateAll()
          .then((isValid) => {
            this.storeState.pulseValid = isValid;
            if (isValid) {
              const obj = JSON.parse(JSON.stringify(this.storeState.selectedPulse));
              this.$set(this.storeState.data.pulse, this.storeState.selectedPulse.id, obj);
              this.storeState.pulseDirty = true;
            }
          });
      });
    },
    clear() {
      this.$nextTick(() => {
        this.storeState.selectedPulse.active = false;
        this.storeState.selectedPulse.pulseWidth = 20;
        this.storeState.selectedPulse.scaler = 1;
        this.storeState.selectedPulse.param = -1;
        if (this.storeState.selectedPulse.out === true) {
          this.storeState.selectedPulse.type = 4;
        } else {
          this.storeState.selectedPulse.type = -1;
        }
        this.storeState.selectedPulse.loadId = -1;
        // console.log(this.storeState.selectedPulse);
        this.validate();
      });
    },
    validate() {
      this.$nextTick(() => {
        this.$validator.validateAll()
          .then((isValid) => {
            this.storeState.pulseValid = isValid;
          });
      });
    },
  },
};
</script>
